import { isUnknownRecord } from "../validation/narrow";

export interface AppErrorData {
  code: string;
  message: string;
  redirectTo?: string;
  deltaCents?: number;
  settlement?: string;
  suggestion?: string;
}

export const ERROR_CODES = {
  AUTH_REQUIRED: "AUTH_REQUIRED",
  FORBIDDEN: "FORBIDDEN",

  VALIDATION_FAILED: "VALIDATION_FAILED",

  BILLING_REQUIRED: "BILLING_REQUIRED",

  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  INVALID_STATE: "INVALID_STATE",
  CONFIRMATION_REQUIRED: "CONFIRMATION_REQUIRED",
  SETTLEMENT_CHOICE_REQUIRED: "SETTLEMENT_CHOICE_REQUIRED",
  DUPLICATE: "DUPLICATE",
  EXPIRED: "EXPIRED",

  RATE_LIMIT: "RATE_LIMIT",
  QUOTA_EXCEEDED: "QUOTA_EXCEEDED",
  FREIGHT_UNPRICED: "FREIGHT_UNPRICED",

  DEPENDENCY_FAILED: "DEPENDENCY_FAILED",

  UNKNOWN: "UNKNOWN",
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES] | string;

export function createAppErrorData(
  message: string,
  overrides: Partial<Omit<AppErrorData, "message">> = {},
): AppErrorData {
  return {
    code: overrides.code ?? ERROR_CODES.UNKNOWN,
    message,
    ...(overrides.redirectTo !== undefined && {
      redirectTo: overrides.redirectTo,
    }),
    ...(overrides.deltaCents !== undefined && {
      deltaCents: overrides.deltaCents,
    }),
    ...(overrides.settlement !== undefined && {
      settlement: overrides.settlement,
    }),
    ...(overrides.suggestion !== undefined && {
      suggestion: overrides.suggestion,
    }),
  };
}

export function isAppErrorWithCode(
  error: unknown,
  code: ErrorCode,
): boolean {
  if (!isUnknownRecord(error)) return false;
  const data = error.data;
  return isUnknownRecord(data) && data.code === code;
}

export function extractAppErrorMessage(
  error: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  if (isUnknownRecord(error)) {
    const data = error.data;
    if (isUnknownRecord(data) && typeof data.message === "string") {
      return data.message;
    }
    const message = error.message;
    if (typeof message === "string" && message.length > 0) {
      return message;
    }
  }
  return fallback;
}
